import { useCallback, useState } from "react";

import { useGlobalContext } from "../Context/GlobalContext";

// =====================================================
// CATALOGUE REQUEST DEDUPLICATION
// Same payload = same API request reuse
// =====================================================

const catalogueInFlightRequests = new Map();
const catalogueResponseCache = new Map();

const CATALOGUE_CACHE_TIME = 10000; // 10 seconds

export const useCatalogue = () => {
  const { api } = useGlobalContext();

  const [restaurantDetails, setRestaurantDetails] = useState(null);

  const [catalogueMenu, setCatalogueMenu] = useState([]);

  const [catalogueLoading, setCatalogueLoading] = useState(false);

  const [catalogueError, setCatalogueError] = useState("");

const getCatalogItems = useCallback(
  async ({
    userid,
    orderType = "home_delivery",
    platform = "",
    forceRefresh = false,
  } = {}) => {
    const cleanUserId = String(userid || "").trim();

    const cleanOrderType = "home_delivery";

    const cleanPlatform = String(platform || "").trim();

    if (!cleanUserId) {
      const message = "Restaurant userid is missing.";

      console.error(message, {
        userid,
        orderType,
        platform,
      });

      setRestaurantDetails(null);
      setCatalogueMenu([]);
      setCatalogueError(message);

      return {
        success: false,
        restaurantDetails: null,
        menu: [],
        recommendedItems: [],
        offerItems: [],
        message,
      };
    }

    // =====================================================
    // FINAL PAYLOAD
    // =====================================================

const payload = {
  userid: cleanUserId,
  orderType: cleanOrderType,
  platform: cleanPlatform,
};

    // Same payload ka same unique key
    const requestKey = JSON.stringify(payload);

    // =====================================================
    // APPLY RESULT TO CURRENT HOOK INSTANCE
    // =====================================================

    const applyResult = (result) => {
      if (result?.success) {
        setRestaurantDetails(result.restaurantDetails || null);

        setCatalogueMenu(Array.isArray(result.menu) ? result.menu : []);

        setCatalogueError("");
      } else {
        setRestaurantDetails(null);
        setCatalogueMenu([]);

        setCatalogueError(
          result?.message || "Unable to fetch catalogue items.",
        );
      }

      return result;
    };

    // =====================================================
    // FORCE REFRESH
    // Retry par old cache use nahi hoga
    // =====================================================

    if (forceRefresh) {
      catalogueResponseCache.delete(requestKey);
    }

    // =====================================================
    // 1. ALREADY COMPLETED SAME REQUEST
    // =====================================================

    if (!forceRefresh) {
      const cachedData = catalogueResponseCache.get(requestKey);

      if (cachedData) {
        const cacheAge = Date.now() - cachedData.timestamp;

        if (cacheAge < CATALOGUE_CACHE_TIME) {
          console.log("♻️ CATALOGUE CACHE USED:", payload);

          return applyResult(cachedData.result);
        }

        catalogueResponseCache.delete(requestKey);
      }
    }

    // =====================================================
    // 2. SAME REQUEST ALREADY RUNNING
    // Second network call MAT KARO
    // =====================================================

    if (!forceRefresh && catalogueInFlightRequests.has(requestKey)) {
      console.log("♻️ EXISTING CATALOGUE REQUEST REUSED:", payload);

      try {
        setCatalogueLoading(true);

        const existingResult = await catalogueInFlightRequests.get(requestKey);

        return applyResult(existingResult);
      } finally {
        setCatalogueLoading(false);
      }
    }

    // =====================================================
    // 3. NEW API REQUEST
    // =====================================================

    setCatalogueLoading(true);
    setCatalogueError("");

    const requestPromise = (async () => {
      try {
        console.log("🚀 GET CATALOG ITEMS API CALL:", payload);

        const response = await api.post(
          "/Order/v3.0/getCatalogItemsDeva",
          payload,
        );

        const responseData = response?.data;

        if (String(responseData?.responseCode) !== "00") {
          return {
            success: false,
            restaurantDetails: null,
            menu: [],
            recommendedItems: [],
            offerItems: [],
            message:
              responseData?.responseMessage ||
              "Unable to fetch catalogue items.",
          };
        }

        const restaurantData =
          responseData?.response?.restaurantDetails || null;

        const menuData = Array.isArray(responseData?.response?.menu)
          ? responseData.response.menu
          : [];

        const recommendedItems = Array.isArray(
          responseData?.response?.recommended,
        )
          ? responseData.response.recommended
          : [];

        const offerItems = Array.isArray(responseData?.response?.offers)
          ? responseData.response.offers
          : [];

        return {
          success: true,

          restaurantDetails: restaurantData,

          menu: menuData,

          recommendedItems,

          offerItems,

          message: responseData?.responseMessage || "",
        };
      } catch (error) {
        console.error(
          "getCatalogItems API error:",
          error?.response?.data || error,
        );

        const message =
          error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to fetch catalogue items.";

        return {
          success: false,
          restaurantDetails: null,
          menu: [],
          recommendedItems: [],
          offerItems: [],
          message,
        };
      }
    })();

    // IMPORTANT:
    // Same payload ka promise globally store
    catalogueInFlightRequests.set(requestKey, requestPromise);

    try {
      const result = await requestPromise;

      // Successful response short time ke liye cache
      if (result?.success) {
        catalogueResponseCache.set(requestKey, {
          result,
          timestamp: Date.now(),
        });
      }

      return applyResult(result);
    } finally {
      // Sirf current promise ko remove karo
      if (catalogueInFlightRequests.get(requestKey) === requestPromise) {
        catalogueInFlightRequests.delete(requestKey);
      }

      setCatalogueLoading(false);
    }
  },
  [api],
);

const getAllRestaurantOffers = useCallback(
  async ({
    userid,
    orderType = "home_delivery",
    platform = "",
    bestFor = "",
  }) => {
    try {
      const result = await getCatalogItems({
        userid,
        orderType,
        platform,
        bestFor,
      });

      if (!result?.success) {
        return {
          success: false,
          offers: [],
          message: result?.message || "Unable to fetch offers.",
        };
      }

      const menu = Array.isArray(result.menu) ? result.menu : [];

      const offers = [];

      menu.forEach((category) => {
        const menuItems = Array.isArray(category?.menu_items)
          ? category.menu_items
          : [];

        menuItems.forEach((item) => {
          const prices = Array.isArray(item?.prices) ? item.prices : [];

          prices.forEach((price) => {
            const actualPrice = Number(price?.price || 0);
            const discountPrice = Number(price?.discount_price || 0);

            const hasOffer =
              Object.prototype.hasOwnProperty.call(price, "discount") ||
              Object.prototype.hasOwnProperty.call(price, "offer") ||
              Object.prototype.hasOwnProperty.call(price, "is_offer") ||
              Object.prototype.hasOwnProperty.call(price, "has_offer");

            if (!hasOffer) {
              return;
            }

            offers.push({
              ...item,

              restaurantUserId: userid,

              restaurantName:
                result?.restaurantDetails?.restaurant_name || "Restaurant",

              restaurantImage:
                result?.restaurantDetails?.restaurant_image ||
                result?.restaurantDetails?.restaurant_profile_image ||
                "",

              categoryName: category?.category_name || "",

              price: actualPrice,
              discountPrice,

              discount: String(price?.discount ?? "").trim(),

              size: price?.size || "",
            });
          });
        });
      });

      return {
        success: true,
        offers,
      };
    } catch (error) {
      console.error("getAllRestaurantOffers error:", error);

      return {
        success: false,
        offers: [],
        message: error?.message || "Unable to fetch restaurant offers.",
      };
    }
  },
  [getCatalogItems],
);

  const resetCatalogue = useCallback(() => {
    setRestaurantDetails(null);
    setCatalogueMenu([]);
    setCatalogueError("");
  }, []);

return {
  restaurantDetails,
  catalogueMenu,
  catalogueLoading,
  catalogueError,

  getCatalogItems,
  getAllRestaurantOffers,
  resetCatalogue,
};
};
